/* Greedy — minimal section interactivity
 * Handles:
 *  - greedy-results-compare slider drag/range
 *  - greedy-shoppable-look hotspot toggle
 *  - presale notice, limited-drop chip and sold-out chip / note in the
 *    vendor buy box and quick view
 * Scoped per section, idempotent on theme-editor reloads.
 */
(function () {
  'use strict';

  function initCompare(root) {
    if (root.__greedyCompareBound) return;
    root.__greedyCompareBound = true;
    var range = root.querySelector('[data-greedy-compare-range]');
    if (!range) return;

    function setPos(value) {
      root.style.setProperty('--greedy-slider-pos', value + '%');
    }
    setPos(range.value);

    range.addEventListener('input', function () {
      setPos(range.value);
    });
  }

  function initShoppable(root) {
    if (root.__greedyShoppableBound) return;
    root.__greedyShoppableBound = true;

    var hotspots = root.querySelectorAll('[data-greedy-hotspot]');
    var cards = root.querySelectorAll('[data-greedy-hotspot-card]');

    function closeAll() {
      hotspots.forEach(function (h) { h.setAttribute('aria-expanded', 'false'); });
      cards.forEach(function (c) { c.setAttribute('aria-hidden', 'true'); });
    }

    hotspots.forEach(function (hotspot) {
      hotspot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var open = hotspot.getAttribute('aria-expanded') === 'true';
        var card = root.querySelector('#' + hotspot.getAttribute('aria-controls'));
        closeAll();
        if (!open && card) {
          hotspot.setAttribute('aria-expanded', 'true');
          card.setAttribute('aria-hidden', 'false');
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) closeAll();
    });
  }

  function initRevealGroup(group) {
    if (group.__greedyRevealBound) return;
    group.__greedyRevealBound = true;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      group.classList.add('is-revealed');
      return;
    }

    Array.prototype.forEach.call(group.children, function (child, i) {
      child.style.setProperty('--greedy-reveal-i', i);
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    observer.observe(group);
  }

  function initCardCarousel(root) {
    if (root.__greedyCardsBound) return;
    root.__greedyCardsBound = true;

    var track = root.querySelector('[data-greedy-cards-track]');
    var next = root.querySelector('[data-greedy-cards-next]');
    if (!track || !next) return;

    function atEnd() {
      return track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
    }
    function sync() {
      next.classList.toggle('is-end', atEnd());
    }

    next.addEventListener('click', function () {
      var card = track.querySelector('.greedy-product-cards__card-wrap');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      var step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      // land exactly on the next snap point so mandatory snapping can't bounce back
      var target = Math.round(track.scrollLeft / step + 1) * step;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: target, behavior: reduced ? 'auto' : 'smooth' });
      window.setTimeout(sync, 600);
    });
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  function initUgcReel(root) {
    if (root.__greedyUgcBound) return;
    root.__greedyUgcBound = true;

    var track = root.querySelector('[data-greedy-ugc-track]');
    if (!track) return;
    var prev = root.querySelector('[data-greedy-ugc-prev]');
    var next = root.querySelector('[data-greedy-ugc-next]');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Arrow paging — land exactly on snap points */
    function step() {
      var item = track.querySelector('.greedy-ugc__item');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    }
    function sync() {
      if (prev) prev.classList.toggle('is-start', track.scrollLeft <= 4);
      if (next) next.classList.toggle('is-end', track.scrollLeft >= track.scrollWidth - track.clientWidth - 4);
    }
    function page(direction) {
      var target = Math.round(track.scrollLeft / step() + direction) * step();
      track.scrollTo({ left: Math.max(0, target), behavior: reduced ? 'auto' : 'smooth' });
      window.setTimeout(sync, 600);
    }
    if (next) next.addEventListener('click', function () { page(1); });
    if (prev) prev.addEventListener('click', function () { page(-1); });
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();

    /* Videos: play muted while in view, pause out of view */
    var videos = Array.prototype.slice.call(track.querySelectorAll('video'));
    videos.forEach(function (video) {
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.removeAttribute('controls');
    });

    if (videos.length && 'IntersectionObserver' in window) {
      var watcher = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.intersectionRatio >= 0.5) {
              var playing = video.play();
              if (playing && playing.catch) playing.catch(function () {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.5] }
      );
      videos.forEach(function (video) { watcher.observe(video); });
    }

    /* Tap-to-play fallback when autoplay is off (reduced motion) */
    if (reduced) {
      videos.forEach(function (video) {
        video.addEventListener('click', function () {
          if (video.paused) { video.play().catch(function () {}); } else { video.pause(); }
        });
      });
    }

    /* Sound toggles — one unmuted video at a time */
    root.querySelectorAll('[data-greedy-ugc-sound]').forEach(function (button) {
      button.addEventListener('click', function () {
        var video = button.closest('.greedy-ugc__media').querySelector('video');
        if (!video) return;
        var unmuting = video.muted;
        if (unmuting) {
          videos.forEach(function (other) {
            if (other !== video) other.muted = true;
          });
          root.querySelectorAll('[data-greedy-ugc-sound]').forEach(function (other) {
            other.setAttribute('aria-pressed', 'false');
            other.setAttribute('aria-label', 'Unmute video');
          });
        }
        video.muted = !unmuting;
        button.setAttribute('aria-pressed', unmuting ? 'true' : 'false');
        button.setAttribute('aria-label', unmuting ? 'Mute video' : 'Unmute video');
        if (unmuting && video.paused) video.play().catch(function () {});
      });
    });
  }

  function initQuickAdd(form) {
    if (form.__greedyQaddBound) return;
    form.__greedyQaddBound = true;

    form.addEventListener('submit', function (event) {
      if (!window.fetch) return; // native POST fallback
      event.preventDefault();

      var btn = form.querySelector('[data-greedy-qadd-btn]');
      var label = form.querySelector('[data-greedy-qadd-label]');
      if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
      var idle = label ? label.textContent : '';

      btn.classList.add('is-loading');
      if (label) label.textContent = 'Adding…';

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var data = new FormData(form);
      var payload = {
        id: Number(data.get('id')),
        quantity: Number(data.get('quantity')) || 1
      };
      // Line-item properties (the presale "Ships:" line) ride along as the
      // cart API's properties object, the same as a native form post would.
      data.forEach(function (value, key) {
        var match = /^properties\[(.+)\]$/.exec(key);
        if (!match) return;
        payload.properties = payload.properties || {};
        payload.properties[match[1]] = value;
      });
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
          window.greedyOpenCart();
          btn.classList.remove('is-loading');
          if (label) label.textContent = 'Added ✓';
          window.setTimeout(function () { if (label) label.textContent = idle; }, 2500);
        })
        .catch(function () {
          btn.classList.remove('is-loading');
          if (label) label.textContent = 'Try again';
          window.setTimeout(function () { if (label) label.textContent = idle; }, 2500);
        });
    });
  }

  // B2B quantity rules (Shopify company price lists set min / increment).
  // Stiletto's steppers are hardcoded to +/-1 and read the input value right
  // before adjusting, so this capture-phase listener runs first and pre-sets
  // the value such that stock theme.js lands exactly on the rule-compliant
  // quantity. Inputs without a rule (retail: min 1, increment 1) are ignored,
  // so retail behavior is byte-for-byte stock.
  function initB2bQuantityRules() {
    document.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var isAdd = event.target.closest('[data-add-quantity]');
      var isSubtract = !isAdd && event.target.closest('[data-subtract-quantity]');
      if (!isAdd && !isSubtract) return;

      var wrapper = (isAdd || isSubtract).closest('.quantity-input');
      if (!wrapper) return;
      var input = wrapper.querySelector('[data-quantity-input]');
      if (!input) return;

      var min = parseInt(input.getAttribute('data-qty-min'), 10) || 1;
      var increment = parseInt(input.getAttribute('data-qty-increment'), 10) || 1;
      if (min <= 1 && increment <= 1) return; // retail: leave stock behavior alone

      var current = parseInt(input.value, 10);
      if (isNaN(current)) current = min;
      var inCartLine = !!wrapper.closest('[data-input-item]');
      var desired;

      if (isAdd) {
        desired = current + increment;
      } else {
        desired = current - increment;
        if (desired < min) {
          // In the cart, stepping below the minimum removes the line;
          // on a product form it just clamps to the minimum.
          desired = inCartLine ? 0 : min;
        }
      }

      // Compensate for theme.js's upcoming +/-1 so it computes `desired`.
      input.value = isAdd ? desired - 1 : desired + 1;
    }, true);
  }
  initB2bQuantityRules();

  // greedy-snap-scroll: full-screen panels in their own snap container.
  // Tracks the active panel for the dots, jumps on dot click, and only lets
  // a panel's video play while that panel is the one in view.
  function initSnapScroll(root) {
    if (root.__greedySnapBound) return;
    root.__greedySnapBound = true;

    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-greedy-snap-panel]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-greedy-snap-dot]'));
    var cue = root.querySelector('.greedy-snap__cue');
    if (!panels.length) return;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function setActive(i) {
      dots.forEach(function (dot, j) {
        dot.classList.toggle('is-active', j === i);
        if (j === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (cue) cue.classList.toggle('is-hidden', i > 0);
    }
    setActive(0);

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        panels[i].scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      });
    });

    if (!('IntersectionObserver' in window)) return;

    var panelObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          setActive(panels.indexOf(entry.target));
        }
      });
    }, { root: root, threshold: [0.6] });
    panels.forEach(function (panel) { panelObserver.observe(panel); });

    var videos = root.querySelectorAll('video');
    if (videos.length && !reduced) {
      var videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            if (video.paused && video.play) video.play().catch(function () {});
          } else if (video.pause) {
            video.pause();
          }
        });
      }, { root: root, threshold: 0.5 });
      videos.forEach(function (video) { videoObserver.observe(video); });
    }
  }

  /* Colour dots beside menu links. The map lives in the "Colour dots" theme
     setting and reaches here as data-greedy-meganav-dots on <html>, one
     "link text | colour" per line. Matching is on the link's visible text,
     case-insensitive, so it works whatever URL the merchant points the link
     at. Runs on the mega menu and the mobile drawer. */
  function initMeganavDots(scope) {
    var raw = document.documentElement.getAttribute('data-greedy-meganav-dots');
    if (!raw) return;
    var map = {};
    raw.split(/\r?\n/).forEach(function (line) {
      var parts = line.split('|');
      if (parts.length < 2) return;
      var label = parts.shift().trim().toLowerCase();
      var colour = parts.join('|').trim().replace(/[<>"]/g, '');
      if (label && colour) map[label] = colour;
    });
    var labels = Object.keys(map);
    if (!labels.length) return;
    /* Longest label first, so "Ballerina Pink" beats "Pink" when both match. */
    labels.sort(function (a, b) { return b.length - a.length; });
    var links = (scope || document).querySelectorAll('.meganav__nav a, .drawer-menu__link');
    Array.prototype.forEach.call(links, function (link) {
      if (link.__greedyDot) return;
      var text = (link.textContent || '').trim().toLowerCase();
      if (!text) return;
      var colour = map[text];
      if (!colour) {
        for (var i = 0; i < labels.length; i++) {
          if (text.indexOf(labels[i]) !== -1) { colour = map[labels[i]]; break; }
        }
      }
      if (!colour) return;
      link.__greedyDot = true;
      var dot = document.createElement('span');
      dot.className = 'greedy-meganav-dot';
      dot.setAttribute('aria-hidden', 'true');
      /* A pasted file URL becomes the dot's image; anything else is a
         colour or gradient. */
      if (/^(https?:)?\/\/|^url\(/i.test(colour)) {
        var src = colour.replace(/^url\((.*)\)$/, '$1').replace(/^['"]|['"]$/g, '');
        dot.style.background = 'url("' + src + '") center / cover no-repeat';
      } else {
        dot.style.background = colour;
      }
      link.insertBefore(dot, link.firstChild);
    });
  }

  /* Presale. The "Greedy presale" switch plus the product tag `Presale` turn
     a product page into a presale. The buy box can only be reached through a
     custom_liquid block, and a liquid-type setting cannot read `settings`
     (Shopify sandboxes it; a snippet rendered from inside one inherits that),
     so the block renders only an empty slot gated on the product tag, and the
     notice is built here from the config layout/theme.liquid emits. The same
     builder fills the quick-view modal, which has no template blocks at all.
     - The line-item property ("Ships: …") is Liquid: the layout renders a
       hidden input form-associated to Stiletto's form by id, so it ships with
       or without script. The script only moves it inside the form if that id
       ever changes.
     - Stiletto's buy button and sticky bar are vendor markup, so the label is
       rewritten here. updateBuyButton() in theme.js re-reads
       data-lang-available on every variant change, so one rewrite keeps
       "Pre-order" through colour switches.
     The layout only emits the config while the switch is on, so without it
     none of this runs and the slot stays empty and unstyled. Nothing is keyed
     off a `greedy-presale` class on <html>: that is the panel's own class. */
  var presaleConfigCache;
  function presaleConfig() {
    if (presaleConfigCache !== undefined) return presaleConfigCache;
    presaleConfigCache = null;
    var el = document.querySelector('[data-greedy-presale-config]');
    if (el) {
      try { presaleConfigCache = JSON.parse(el.textContent); } catch (e) { presaleConfigCache = null; }
    }
    return presaleConfigCache;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function buildPresalePanel(cfg, extraClass) {
    var panel = document.createElement('div');
    panel.className = 'greedy-presale' + (extraClass ? ' ' + extraClass : '');
    panel.setAttribute('data-greedy-presale', '');
    var html = '';
    if (cfg.eyebrow) html += '<p class="greedy-presale__eyebrow">' + escapeHtml(cfg.eyebrow) + '</p>';
    if (cfg.line) html += '<p class="greedy-presale__line">' + escapeHtml(cfg.line) + '</p>';
    // The note is the merchant's richtext setting, the same HTML Liquid prints.
    if (cfg.note) html += '<div class="greedy-presale__note rte">' + cfg.note + '</div>';
    panel.innerHTML = html;
    return panel;
  }

  function presaleRelabel(form, label) {
    Array.prototype.forEach.call(form.querySelectorAll('[data-add-to-cart]'), function (btn) {
      btn.setAttribute('data-lang-available', label);
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      var text = btn.querySelector('[data-add-to-cart-text]');
      if (text) text.textContent = label;
      btn.setAttribute('aria-label', label);
    });
  }

  // Make sure `form` carries the "Ships:" property: adopt the layout's input
  // if it is not already associated with this form, else create one.
  function presaleEnsureProperty(form, cfg) {
    if (!cfg.property) return;
    if (form.querySelector('input[name="properties[Ships]"]')) return;
    var input = document.querySelector('[data-greedy-presale-input]');
    if (input && input.form === form) return;
    if (input && !input.form) {
      input.removeAttribute('form');
      form.appendChild(input);
      return;
    }
    var made = document.createElement('input');
    made.type = 'hidden';
    made.name = 'properties[Ships]';
    made.value = cfg.property;
    form.appendChild(made);
  }

  function initPresale(scope) {
    var cfg = presaleConfig();
    if (!cfg) return;
    var root = scope || document;
    var slots = Array.prototype.slice.call(root.querySelectorAll('[data-greedy-presale-slot]'));
    if (root !== document && root.matches && root.matches('[data-greedy-presale-slot]')) slots.unshift(root);
    slots.forEach(function (slot) {
      if (slot.__greedyPresale) return;
      slot.__greedyPresale = true;
      slot.appendChild(buildPresalePanel(cfg));
      var container = slot.closest('[data-section-id]') || document;
      var form = container.querySelector('form[data-product-form]');
      if (!form) return;
      presaleEnsureProperty(form, cfg);
      if (cfg.label) presaleRelabel(form, cfg.label);
    });
  }

  /* Quick view: the modal gets the same panel, keyed off the PRESALE badge it
     renders (the same tag, through custom badge group 4). */
  function initPresaleQuickView(el) {
    var cfg = presaleConfig();
    if (!cfg || el.__greedyPresale) return;
    if (!el.querySelector('.product-badge[data-handle="presale"]')) return;
    var form = el.querySelector('form[data-product-form]');
    if (!form) return;
    el.__greedyPresale = true;
    form.parentNode.insertBefore(buildPresalePanel(cfg, 'greedy-presale--quick'), form);
    if (cfg.property) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Ships]';
      input.value = cfg.property;
      form.appendChild(input);
    }
    if (cfg.label) presaleRelabel(form, cfg.label);
  }

  /* Limited drop. "Limited drop colours" (Theme settings → Greedy → Product
     page) names option values, e.g. Chili Red. While one of them is the
     selected colour, the vendor picker's "Color: Chili Red" label gains a chip
     and a one-line note sits under the swatches. Stiletto stamps every swatch
     and every <option> in the hidden select with "<value handle>--<n>"
     (product-swatch.liquid, product-block-variant-picker.liquid), mirrors a
     swatch click into that select and fires `change` on it: that is the
     hook, so nothing here depends on theme.js's custom events (off by
     default in theme-globals.liquid). That synthetic event is `new
     Event("change")`, which does not bubble, so the listener sits on the
     select itself, not on the wrapper or the form. Quick view renders the
     same picker, so the same init runs on the modal. The config comes from
     layout/theme.liquid and is only there while the setting names a colour. */
  var limitedConfigCache;
  function limitedConfig() {
    if (limitedConfigCache !== undefined) return limitedConfigCache;
    limitedConfigCache = null;
    var el = document.querySelector('[data-greedy-limited-config]');
    if (el) {
      try { limitedConfigCache = JSON.parse(el.textContent); } catch (e) { limitedConfigCache = null; }
    }
    if (limitedConfigCache && (!Array.isArray(limitedConfigCache.handles) || !limitedConfigCache.handles.length)) {
      limitedConfigCache = null;
    }
    return limitedConfigCache;
  }

  function limitedHandle(stamped) {
    return String(stamped || '').replace(/--\d+$/, '');
  }

  function limitedSelectedHandle(option) {
    var select = option.querySelector('select');
    var chosen = select && select.options[select.selectedIndex];
    return chosen ? limitedHandle(chosen.getAttribute('data-value-handle')) : '';
  }

  function initLimited(scope) {
    var cfg = limitedConfig();
    if (!cfg) return;
    var root = scope || document;
    var pickers = Array.prototype.slice.call(root.querySelectorAll('.product__variants-wrapper'));
    if (root !== document && root.matches && root.matches('.product__variants-wrapper')) pickers.unshift(root);
    pickers.forEach(function (picker) {
      if (picker.__greedyLimited) return;
      picker.__greedyLimited = true;
      var rows = [];
      // The option row only: the snippet also stamps data-product-option on
      // the nested swatch / chip container, which has no label of its own.
      Array.prototype.forEach.call(picker.querySelectorAll('.product__option[data-product-option]'), function (option) {
        // Only an option that offers a limited value gets the chip and note.
        var offers = false;
        Array.prototype.forEach.call(option.querySelectorAll('[data-value-handle]'), function (opt) {
          if (cfg.handles.indexOf(limitedHandle(opt.getAttribute('data-value-handle'))) !== -1) offers = true;
        });
        if (!offers) return;

        var chip = document.createElement('span');
        chip.className = 'greedy-chip greedy-limited-chip';
        chip.textContent = cfg.label;
        chip.hidden = true;
        var label = option.querySelector('.product__label');
        if (label) label.appendChild(chip);

        var note = null;
        if (cfg.note) {
          note = document.createElement('p');
          note.className = 'greedy-limited-note';
          note.textContent = cfg.note;
          note.hidden = true;
          option.appendChild(note);
        }

        // The swatch itself: a hook for CSS and the chip text for a screen
        // reader, so the drop is findable before it is selected.
        Array.prototype.forEach.call(option.querySelectorAll('[data-option-handle]'), function (btn) {
          if (cfg.handles.indexOf(limitedHandle(btn.getAttribute('data-option-handle'))) === -1) return;
          btn.setAttribute('data-greedy-limited', '');
          var aria = btn.getAttribute('aria-label');
          if (aria && aria.indexOf(cfg.label) === -1) btn.setAttribute('aria-label', aria + ' (' + cfg.label + ')');
        });

        rows.push({ option: option, chip: chip, note: note, select: option.querySelector('select') });
      });
      if (!rows.length) return;

      function sync() {
        rows.forEach(function (row) {
          var on = cfg.handles.indexOf(limitedSelectedHandle(row.option)) !== -1;
          row.chip.hidden = !on;
          if (row.note) row.note.hidden = !on;
          row.option.classList.toggle('greedy-limited-selected', on);
        });
      }
      sync();
      rows.forEach(function (row) {
        if (row.select) row.select.addEventListener('change', sync);
      });
    });
  }

  /* Sold out. Stiletto's own availability pass only adds a `disabled` class
     to the swatch, which the stock CSS barely draws, and says nothing beside
     the colour name — so a sold-out colour reads as buyable until the button
     changes. This adds the chip, the note and the optional notify link, and
     tags the swatch for CSS. Which colours are sold out is read from the
     variant JSON the picker already prints, so it follows real stock and no
     setting names a colour. */
  var soldOutConfigCache;
  function soldOutConfig() {
    if (soldOutConfigCache !== undefined) return soldOutConfigCache;
    soldOutConfigCache = null;
    var el = document.querySelector('[data-greedy-soldout-config]');
    if (el) {
      try { soldOutConfigCache = JSON.parse(el.textContent); } catch (e) { soldOutConfigCache = null; }
    }
    if (soldOutConfigCache && !soldOutConfigCache.label) soldOutConfigCache = null;
    return soldOutConfigCache;
  }

  function soldOutVariants(picker) {
    var el = picker.querySelector('[data-variant-json]');
    if (!el) return null;
    var list;
    try { list = JSON.parse(el.textContent); } catch (e) { return null; }
    return Array.isArray(list) && list.length ? list : null;
  }

  // The select carries data-index="optionN"; the variant's own options array
  // is zero-based, so N-1 indexes it.
  function soldOutIndex(select) {
    var match = /(\d+)\s*$/.exec(select.getAttribute('data-index') || '');
    return match ? parseInt(match[1], 10) - 1 : -1;
  }

  /* The buy button's stale sold-out state.

     product-form.liquid stamps `aria-disabled="true"` next to `disabled` when
     the page renders on a colour that cannot be sold, and sets aria-label to
     the sold-out string. theme.js's updateBuyButton then clears `disabled` on
     a variant change and rewrites the visible text — but it never touches
     either aria attribute. So a page that loaded on a sold-out colour (any
     ?variant= link, and Stiletto puts one in the URL as soon as a swatch is
     clicked) keeps a buy button that is clickable but reads as disabled to
     assistive tech, and which the disabled rule in greedy-stiletto.css paints
     at half opacity with a not-allowed cursor. It looks broken because it is
     still claiming to be. Keep the attributes honest from the variant JSON,
     which is the same source the rest of this pass uses. `disabled` itself is
     left to theme.js: it owns that, and it gets it right. */
  function soldOutScope(picker) {
    return picker.closest('[data-is-quick-view]') ||
      picker.closest('.shopify-section') ||
      document;
  }

  function soldOutSyncBuyButtons(picker, available) {
    var scope = soldOutScope(picker);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-add-to-cart]'), function (btn) {
      var label = available
        ? btn.getAttribute('data-lang-available')
        : btn.getAttribute('data-lang-sold-out');
      if (available) {
        btn.removeAttribute('aria-disabled');
      } else {
        btn.setAttribute('aria-disabled', 'true');
      }
      if (label) btn.setAttribute('aria-label', label);
    });
  }

  function initSoldOut(scope) {
    var cfg = soldOutConfig();
    if (!cfg) return;
    var root = scope || document;
    var pickers = Array.prototype.slice.call(root.querySelectorAll('.product__variants-wrapper'));
    if (root !== document && root.matches && root.matches('.product__variants-wrapper')) pickers.unshift(root);
    pickers.forEach(function (picker) {
      if (picker.__greedySoldOut) return;
      picker.__greedySoldOut = true;
      var variants = soldOutVariants(picker);
      if (!variants) return;

      var rows = [];
      // The option row only: the snippet also stamps data-product-option on
      // the nested swatch / chip container, which has no label of its own.
      Array.prototype.forEach.call(picker.querySelectorAll('.product__option[data-product-option]'), function (option) {
        var select = option.querySelector('select');
        if (!select) return;
        var index = soldOutIndex(select);
        if (index < 0) return;

        var chip = document.createElement('span');
        chip.className = 'greedy-chip greedy-soldout-chip';
        chip.textContent = cfg.label;
        chip.hidden = true;
        var label = option.querySelector('.product__label');
        if (label) label.appendChild(chip);

        var note = null;
        if (cfg.note) {
          note = document.createElement('p');
          note.className = 'greedy-soldout-note';
          note.textContent = cfg.note;
          note.hidden = true;
          option.appendChild(note);
        }

        var notify = null;
        if (cfg.notifyUrl && cfg.notifyLabel) {
          notify = document.createElement('a');
          notify.className = 'greedy-soldout-notify';
          notify.href = cfg.notifyUrl;
          notify.textContent = cfg.notifyLabel;
          notify.hidden = true;
          option.appendChild(notify);
        }

        var buttons = Array.prototype.slice.call(option.querySelectorAll('[data-option-value][data-option-handle]'));
        buttons.forEach(function (btn) {
          btn.__greedyAria = btn.getAttribute('aria-label') || '';
        });

        rows.push({
          option: option,
          select: select,
          index: index,
          chip: chip,
          note: note,
          notify: notify,
          buttons: buttons
        });
      });
      if (!rows.length) return;

      function sync() {
        // What is picked on every option right now, so a combination that is
        // sold out reads as sold out even when each value alone is stocked.
        var chosen = [];
        rows.forEach(function (row) { chosen[row.index] = row.select.value; });

        function available(index, value) {
          for (var i = 0; i < variants.length; i++) {
            var v = variants[i];
            if (!v || !v.available || !v.options) continue;
            if (v.options[index] !== value) continue;
            var matches = true;
            for (var j = 0; j < chosen.length; j++) {
              if (j === index || chosen[j] == null) continue;
              if (v.options[j] !== chosen[j]) { matches = false; break; }
            }
            if (matches) return true;
          }
          return false;
        }

        rows.forEach(function (row) {
          row.buttons.forEach(function (btn) {
            var out = !available(row.index, btn.getAttribute('data-option-value'));
            if (out) {
              btn.setAttribute('data-greedy-soldout', '');
              if (btn.__greedyAria) btn.setAttribute('aria-label', btn.__greedyAria + ' (' + cfg.label + ')');
            } else {
              btn.removeAttribute('data-greedy-soldout');
              if (btn.__greedyAria) btn.setAttribute('aria-label', btn.__greedyAria);
            }
          });
          var on = !available(row.index, row.select.value);
          row.chip.hidden = !on;
          if (row.note) row.note.hidden = !on;
          if (row.notify) row.notify.hidden = !on;
          row.option.classList.toggle('greedy-soldout-selected', on);
        });

        // The whole combination, not one option: the buy button is for the
        // variant. No match (a combination the product does not carry) leaves
        // the attributes alone — theme.js already disables the button there.
        var chosenVariant = null;
        for (var v = 0; v < variants.length && !chosenVariant; v++) {
          var candidate = variants[v];
          if (!candidate || !candidate.options) continue;
          var matches = true;
          for (var k = 0; k < chosen.length; k++) {
            if (chosen[k] == null) continue;
            if (candidate.options[k] !== chosen[k]) { matches = false; break; }
          }
          if (matches) chosenVariant = candidate;
        }
        if (chosenVariant) soldOutSyncBuyButtons(picker, !!chosenVariant.available);
      }
      sync();
      // On the select, not the wrapper: theme.js fires a synthetic change
      // there and it does not bubble.
      rows.forEach(function (row) { row.select.addEventListener('change', sync); });
    });
  }

  /* Quick view: the modal is injected by theme.js, so the presale panel, the
     limited-drop chip and the sold-out treatment all wait for it here. */
  function initQuickViewObserver() {
    if (!window.MutationObserver) return;
    var presale = !!presaleConfig();
    var limited = !!limitedConfig();
    var soldOut = !!soldOutConfig();
    if (!presale && !limited && !soldOut) return;
    var observer = new MutationObserver(function (records) {
      records.forEach(function (record) {
        Array.prototype.forEach.call(record.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          var el = node.matches('[data-is-quick-view]') ? node : node.querySelector('[data-is-quick-view]');
          if (!el) return;
          if (presale) initPresaleQuickView(el);
          if (limited) initLimited(el);
          if (soldOut) initSoldOut(el);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initAll(scope) {
    initMeganavDots(scope);
    initPresale(scope);
    initLimited(scope);
    initSoldOut(scope);
    (scope || document).querySelectorAll('[data-greedy-snap]').forEach(initSnapScroll);
    (scope || document).querySelectorAll('[data-greedy-compare]').forEach(initCompare);
    (scope || document).querySelectorAll('[data-greedy-shoppable]').forEach(initShoppable);
    (scope || document).querySelectorAll('[data-greedy-reveal-group]').forEach(initRevealGroup);
    (scope || document).querySelectorAll('[data-greedy-cards]').forEach(initCardCarousel);
    (scope || document).querySelectorAll('[data-greedy-ugc]').forEach(initUgcReel);
    (scope || document).querySelectorAll('[data-greedy-qadd]').forEach(initQuickAdd);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); initQuickViewObserver(); });
  } else {
    initAll();
    initQuickViewObserver();
  }

  // Theme editor: re-init when sections are loaded/reordered
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:reorder', function (e) { initAll(e.target); });

  // Toggle body.greedy-cart-open whenever the cart app's drawer is visible.
  // CSS uses this class to hide overlays (Smile launcher, etc) that
  // would otherwise sit on top of the cart.
  function initCartOpenWatcher() {
    var DRAWER_SELECTORS = '#rebuy-cart, .rebuy-cart__flyout, [id^="upcart"], [class*="upcart"], .Upcart-Cart, .upcart-cart, .upcart-drawer';
    var BODY_CLASS = 'greedy-cart-open';
    var lastState = null;

    function isDrawerOpen() {
      var nodes = document.querySelectorAll(DRAWER_SELECTORS);
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        // Skip the embed/script tags injected by the app block
        if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') continue;
        var rect = el.getBoundingClientRect();
        if (rect.width > 100 && rect.height > 100) {
          var style = window.getComputedStyle(el);
          if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0.1) {
            return true;
          }
        }
      }
      return false;
    }

    function syncBodyClass() {
      var open = isDrawerOpen();
      if (open === lastState) return;
      lastState = open;
      document.body.classList.toggle(BODY_CLASS, open);
    }

    var mo = new MutationObserver(function () {
      // Throttle: run once per animation frame
      if (mo._raf) return;
      mo._raf = requestAnimationFrame(function () {
        mo._raf = null;
        syncBodyClass();
      });
    });
    mo.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['style', 'class', 'aria-hidden'] });
    syncBodyClass();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartOpenWatcher);
  } else {
    initCartOpenWatcher();
  }

  // The cart is Rebuy Smart Cart. Stiletto's own drawer is switched off
  // (Overlay group → Quick cart) and its purchase-confirmation popup with it,
  // so the theme's quick-adds call this after a successful /cart/add.js.
  // Rebuy also opens itself on the adds it sees; show() on an open cart is a
  // no-op, so calling it here as well can't produce a second drawer. Upcart
  // is kept as a fallback for as long as its API might still be on the page.
  window.greedyOpenCart = function () {
    var smartCart = window.Rebuy && window.Rebuy.SmartCart;
    if (smartCart && typeof smartCart.show === 'function') {
      smartCart.show();
    } else if (window.upcartAPI && typeof window.upcartAPI.openCart === 'function') {
      window.upcartAPI.openCart();
    }
  };

  // With the Stiletto drawer gone, the header bag icon falls back to a plain
  // link to /cart. Open the smart cart instead when it is ready; if Rebuy
  // hasn't loaded, the link still goes to the cart page.
  document.addEventListener('click', function (e) {
    var icon = e.target.closest && e.target.closest('[data-js-cart-icon]');
    if (!icon) return;
    var smartCart = window.Rebuy && window.Rebuy.SmartCart;
    if (!smartCart || typeof smartCart.show !== 'function') return;
    e.preventDefault();
    smartCart.show();
  });
})();
